import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usersAPI, authAPI, formatApiError } from '../services/api';
import PageShell from '../components/PageShell';
import { PillInput, PillButton, PillRow } from '../components/PillKit';
import {
  User, At, FileText, Phone, ShieldCheck, Bell,
  Lock, Trash, FileArrowDown, SignOut, EnvelopeSimple, Camera
} from '@phosphor-icons/react';

const PALETTE = '#22C55E';

export default function SettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
    phone: user?.phone || '',
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await usersAPI.updateProfile(form);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <PageShell testId="settings-page" back title={{ line1: 'Edit Profile', line2: 'Your account & security' }}>
      {/* Avatar */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-20 h-20 rounded-full border-[3px] border-white bg-black flex items-center justify-center text-2xl font-extrabold text-[#22C55E] overflow-hidden">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            (user?.name?.[0] || 'U').toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-extrabold text-lg truncate">{user?.name}</p>
          <p className="text-white/50 text-sm truncate">{user?.email}</p>
          {user?.email_verified ? (
            <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider font-bold text-[#22C55E]">
              <ShieldCheck className="w-3 h-3" weight="fill" /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider font-bold text-[#F59E0B]">
              <EnvelopeSimple className="w-3 h-3" /> Email not verified
            </span>
          )}
        </div>
      </div>

      <form onSubmit={save} className="space-y-5 max-w-md mb-8">
        <Field label="Display Name" icon={User}>
          <PillInput value={form.name} onChange={set('name')} placeholder="Your name" data-testid="settings-name" />
        </Field>
        <Field label="Username">
          <div className="relative">
            <At className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
            <PillInput value={form.username} onChange={set('username')} placeholder="username" className="pl-14" data-testid="settings-username" />
          </div>
          {form.username && <p className="text-xs text-white/40 mt-2">divispend.com/{form.username}</p>}
        </Field>
        <Field label="Bio" icon={FileText}>
          <PillInput value={form.bio} onChange={set('bio')} placeholder="Tell people about yourself" data-testid="settings-bio" />
        </Field>
        <Field label="Phone" icon={Phone}>
          <PillInput value={form.phone} onChange={set('phone')} placeholder="+1 (555) 555-5555" data-testid="settings-phone" />
        </Field>
        <PillButton onClick={save} disabled={saving} data-testid="settings-save-btn">
          {saving ? 'Saving…' : 'Save Changes'}
        </PillButton>
      </form>

      {/* Quick actions */}
      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-3">Security & Privacy</h2>
      <div className="space-y-3 max-w-md mb-8">
        <PillRow icon={Lock}        label="Change password"      to="/forgot-password"     testId="settings-change-password" />
        <PillRow icon={Phone}       label="Verify phone number"  to="/verify-phone"        testId="settings-verify-phone" />
        <PillRow icon={ShieldCheck} label="Two-factor auth (2FA)" to="/profile?tab=security" testId="settings-2fa" />
        <PillRow icon={Bell}        label="Notification preferences" to="/profile?tab=notifications" testId="settings-notifications" />
      </div>

      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-3">Data</h2>
      <div className="space-y-3 max-w-md mb-8">
        <PillRow icon={FileArrowDown} label="Download my data" to="/privacy_policy" testId="settings-download" />
        <PillRow icon={Trash}         label="Delete my account" to="/privacy_policy" testId="settings-delete" />
      </div>

      <div className="max-w-md">
        <button
          onClick={handleLogout}
          className="w-full bg-black border-[3px] border-red-500 text-red-500 rounded-full px-6 py-4 text-base font-bold tracking-tight active:bg-red-500/10 flex items-center justify-center gap-2"
          data-testid="settings-logout-btn"
        >
          <SignOut className="w-5 h-5" /> Log Out
        </button>
      </div>
    </PageShell>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-2 block flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: PALETTE }} weight="fill" />}
        {label}
      </label>
      {children}
    </div>
  );
}
